import { useCallback, useState } from 'react';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import * as labelApi from 'utils/labelApi';

// Loads a single label plus its paginated assigned-merchant list, for the
// label detail page (RAT-1699). Mirrors useLabels.js's local-state approach -
// no dedicated slice/thunk for this small, admin-curated resource.
const useLabelDetail = () => {
  const [label, setLabel] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [isLabelLoading, setIsLabelLoading] = useState(false);
  const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const errorMessage = (error, fallback) =>
    error?.message || error?.error?.message || fallback;

  const fetchLabel = useCallback(async (id) => {
    setIsLabelLoading(true);
    try {
      const data = await labelApi.getLabelById(id);
      setLabel(data);
    } catch (error) {
      showErrorAlert(errorMessage(error, 'Failed to load label'));
    } finally {
      setIsLabelLoading(false);
    }
  }, []);

  const fetchMerchants = useCallback(async (id, { page = 1 } = {}) => {
    setIsMerchantsLoading(true);
    try {
      const data = await labelApi.getLabelMerchants(id, { page });
      setMerchants(data?.list || []);
      setTotal(data?.total || 0);
      setLimit(data?.limit || 10);
    } catch (error) {
      showErrorAlert(errorMessage(error, 'Failed to load assigned merchants'));
    } finally {
      setIsMerchantsLoading(false);
    }
  }, []);

  const deleteLabel = useCallback(async (id) => {
    setIsDeleting(true);
    try {
      await labelApi.deleteLabel(id);
      showSuccessAlert('Label deleted');
      return true;
    } catch (error) {
      showErrorAlert(errorMessage(error, 'Failed to delete label'));
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    label,
    merchants,
    total,
    limit,
    isLabelLoading,
    isMerchantsLoading,
    isDeleting,
    fetchLabel,
    fetchMerchants,
    deleteLabel,
  };
};

export default useLabelDetail;
